import { Injectable } from "@angular/core";
import { Router, ActivatedRouteSnapshot } from "@angular/router";
import { RegistrationService } from "../registration/registration.service";
import { HotToastService } from "@ngneat/hot-toast";

/**
 * Guard that ensures the registration/join route has a valid UUID parameter
 * and that the invite exists. If no UUID is present or invite is invalid,
 * shows an error message and redirects to login.
 */
@Injectable({ providedIn: "root" })
export class InviteUuidGuard {
  constructor(
    public router: Router,
    private registrationService: RegistrationService,
    private toastr: HotToastService
  ) {}

  /**
   * Checks if the route has a UUID parameter and validates the invite exists.
   * If not, shows an error message and redirects to login.
   */
  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const uuid = route.params["uuid"];

    if (!uuid || uuid.trim() === "") {
      this.toastr.error("Invalid or expired invite link");
      this.router.navigate(["/login"]);
      return false;
    }

    try {
      const response = await this.registrationService.check_invitation(uuid);

      // Check if invite exists and is valid
      if (response.invites == null || response.invites === undefined) {
        this.toastr.error("Invalid or expired invite link");
        this.router.navigate(["/login"]);
        return false;
      }

      // When looking up by uuid, API returns a single invite object
      const invite = Array.isArray(response.invites)
        ? response.invites[0]
        : response.invites;
      if (invite?.accepted === true) {
        this.toastr.error("This invite has already been used. Please sign in.");
        this.router.navigate(["/login"]);
        return false;
      }

      return true;
    } catch (error) {
      // If invite doesn't exist or API call fails, show error and redirect to login
      const errorMessage = error?.error?.message || "Invalid or expired invite link";
      this.toastr.error(errorMessage);
      this.router.navigate(["/login"]);
      return false;
    }
  }
}
